import { createPortal } from 'react-dom'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

export interface InferenceTypeMeta { icon: string, label: string, style: React.CSSProperties }

export function InferenceTypeBadge ({ tag, reasoning }: { tag: InferenceTypeMeta, reasoning?: string }): React.ReactElement {
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [placement, setPlacement] = useState<'below' | 'above'>('below')
  const hasReasoning = reasoning != null && reasoning !== ''
  const open = hasReasoning && (hovered || pinned)

  useEffect(() => {
    if (open) setRect(anchorRef.current?.getBoundingClientRect() ?? null)
  }, [open])

  useLayoutEffect(() => {
    if (!open || (rect == null)) return
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0
    const spaceBelow = window.innerHeight - rect.bottom
    setPlacement(spaceBelow < popoverHeight + 12 ? 'above' : 'below')
  }, [open, rect])

  useEffect(() => {
    if (!pinned) return
    function handleClickOutside (e: MouseEvent): void {
      if (anchorRef.current?.contains(e.target as Node) !== true) setPinned(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [pinned])

  return (
    <span
      ref={anchorRef}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 10,
        fontWeight: 600,
        padding: '1px 5px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        cursor: hasReasoning ? 'pointer' : 'default',
        ...tag.style
      }}
      onMouseEnter={() => { if (hasReasoning) setHovered(true) }}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { if (hasReasoning) setPinned((p) => !p) }}
    >
      {tag.icon} {tag.label}
      {open && (rect != null) && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            ...(placement === 'below'
              ? { top: rect.bottom + 6 }
              : { top: 'auto', bottom: window.innerHeight - rect.top + 6 }
            ),
            left: Math.min(rect.left, window.innerWidth - 320),
            width: 300,
            zIndex: 1000,
            background: 'var(--bp-surface)',
            border: '1.5px solid var(--bp-border)',
            borderRadius: 8,
            padding: '10px 12px',
            boxShadow: '0 12px 32px rgba(0,0,0,.18)'
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--bp-ink2)', lineHeight: 1.5 }}>
            💡 {reasoning}
          </div>
        </div>,
        document.body
      )}
    </span>
  )
}
